import { isAllDayEvent } from '../../../utils/eventDates';

/**
 * The date and times of an event as a person enters them — one day, whether
 * it runs all day, and a start and optional end time — and the conversion to
 * and from the `startAt`/`endAt` the API stores.
 *
 * An event with no time of its own is stored starting at local midnight with
 * no end. An end time earlier than the start means the event runs past
 * midnight (a wedding from 7 PM to 1 AM), so it lands on the next day.
 */
export interface EventWhen {
  /** `YYYY-MM-DD`, browser-local. */
  date: string;
  allDay: boolean;
  /** `HH:mm`; ignored when `allDay`. */
  startTime: string;
  /** `HH:mm` or empty; ignored when `allDay`. */
  endTime: string;
  /** `YYYY-MM-DD` of a multi-day event's last day, or empty. */
  endDate: string;
}

export const EMPTY_WHEN: EventWhen = {
  date: '',
  allDay: false,
  startTime: '18:00',
  endTime: '',
  endDate: '',
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function localDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localTime(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function at(date: string, time: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const [h, min] = time.split(':').map(Number);
  return new Date(y, m - 1, d, h, min);
}

/** The form's fields for an existing event. */
export function whenFromEvent(
  startAt: Date | string | null | undefined,
  endAt?: Date | string | null,
): EventWhen {
  if (!startAt) return EMPTY_WHEN;
  const start = new Date(startAt);
  const end = endAt ? new Date(endAt) : null;
  const allDay = isAllDayEvent(start, end);
  const nextDay = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
  // An end on the following morning is a late night, not a second day.
  const spansDays =
    !!end &&
    localDate(end) !== localDate(start) &&
    !(localDate(end) === localDate(nextDay) && !allDay && localTime(end) < localTime(start));
  return {
    date: localDate(start),
    allDay,
    startTime: allDay ? EMPTY_WHEN.startTime : localTime(start),
    endTime: end && !allDay ? localTime(end) : '',
    endDate: spansDays && end ? localDate(end) : '',
  };
}

export type EventWhenResult =
  | { ok: true; startAt: Date; endAt: Date | null }
  | { ok: false; error: string };

/** `startAt`/`endAt` for the fields, or why they cannot be saved. */
export function buildEventWhen(when: EventWhen): EventWhenResult {
  if (!when.date) return { ok: false, error: 'Pick a date' };
  if (when.allDay) {
    const startAt = at(when.date, '00:00');
    if (!when.endDate) return { ok: true, startAt, endAt: null };
    if (when.endDate < when.date) return { ok: false, error: 'The last day is before the first' };
    return { ok: true, startAt, endAt: at(when.endDate, '23:59') };
  }
  if (!when.startTime) return { ok: false, error: 'Pick a start time' };
  const startAt = at(when.date, when.startTime);
  if (when.endDate) {
    if (when.endDate < when.date) return { ok: false, error: 'The last day is before the first' };
    const endAt = at(when.endDate, when.endTime || '23:59');
    if (endAt < startAt) return { ok: false, error: 'The end is before the start' };
    return { ok: true, startAt, endAt };
  }
  if (!when.endTime) return { ok: true, startAt, endAt: null };
  const endAt = at(when.date, when.endTime);
  // Ending "earlier" than it starts means it runs past midnight.
  if (endAt <= startAt) endAt.setDate(endAt.getDate() + 1);
  return { ok: true, startAt, endAt };
}

/** True when the end time falls on the day after the start. */
export function endsNextDay(when: EventWhen): boolean {
  return !when.allDay && !when.endDate && !!when.endTime && when.endTime <= when.startTime;
}
